/**
 * `aiConfig.ts` — AI harness endpoints + gates.
 *
 * Mirrors the AI slice of Unity's `CloudSettings` (snake-cased JSON in
 * `AppConfiguration/Application/{env}/apig-app-config.json`, loaded through
 * `AppConfig.cs` post AI-626 "move AI secrets to AI config"):
 *
 *   ai_enabled                             → aiEnabled
 *   cloud_settings.aisvc_host              → aiSvcHost
 *   cloud_settings.campfire_realtime_key_minting_app_url → realtimeKeyMintingUrl
 *   cloud_settings.campfire_minting_app_key              → mintingAppKey (env-only)
 *
 * Host/urls are safe to commit (same policy as `cloudConfig.ts`); secrets are
 * **never** committed here — provide them via `.env.local` when needed:
 *
 *   VITE_AI_ENABLED=true|false                 (absent = enabled)
 *   VITE_AISVC_HOST=...
 *   VITE_CAMPFIRE_REALTIME_KEY_MINTING_APP_URL=...
 *   VITE_CAMPFIRE_MINTING_APP_KEY=...          (only for non-campfire hosts)
 *
 * The OpenAI Responses bearer key (`cloud_settings.campfire_open_ai_api_key`)
 * lives ONLY server-side: the Vite dev proxy injects `Authorization` at
 * server runtime, and the client-dev nginx proxy injects it from a
 * root-owned include on the box. The client bundle carries just the boolean
 * capability flag `VITE_CAMPFIRE_AI_RESPONSES_AUTH` (seeded by
 * `vite.config.ts` from server-side key presence).
 *
 * Auth model (ports `AIAgentTransportWebSocket.MintEphemeralKeyAsync`):
 * campfire3d.com AI proxy hosts authenticate server-side, so the client
 * neither mints an ephemeral key nor sends `Authorization` (Unity skips the
 * header for those hosts — sending it errors). For direct OpenAI hosts the
 * browser must mint an ephemeral key and pass it via the WebSocket
 * subprotocol (`openai-insecure-api-key.{key}`), since browsers cannot set
 * WebSocket request headers.
 */

export interface AiConfig {
  /** Master AI gate — port of `AppConfig.AIEnabled` (`ai_enabled`, default enabled). */
  readonly aiEnabled: boolean;
  /** Realtime + Responses host (`cloud_settings.aisvc_host`). */
  readonly aiSvcHost: string;
  /** Ephemeral realtime key mint endpoint (unused for campfire hosts). */
  readonly realtimeKeyMintingUrl: string;
  /** `X-App-Key` for the mint endpoint (env-only secret). */
  readonly mintingAppKey: string;
  /**
   * Whether the same-origin Responses proxy can authenticate upstream
   * (`/v1/responses` 401s keyless on EVERY host, campfire proxy included).
   * The key itself never reaches the client — this is a capability flag,
   * defaulting FALSE (fail closed: review/chat degrade to procedural
   * findings, matching the old missing-key behavior).
   */
  readonly responsesAuthAvailable: boolean;
}

const DEFAULT_AISVC_HOST = "oaiproxy-dev.campfire3d.com";
const DEFAULT_MINT_URL = "https://ai-dev.campfire3d.com/api/realtime-key";
/**
 * Campfire's realtime-key minting app key, DEV tier.
 *
 * ⚠ Committed on purpose (direction call, 2026-08-28), unlike the OpenAI API
 * key — which must never reach a browser, and which the deploy script aborts on
 * if it appears in `dist/`. This one is NARROW by comparison: its only power is
 * to mint short-lived realtime tokens from Campfire's own mint service, that
 * service checks the caller's origin, and Unity already ships the same value in
 * `AppConfiguration/Application/dev/ai-app-config.json`.
 *
 * Committing it is what makes the WebRTC transport work with NO server-side
 * infrastructure: the browser mints a token and posts its SDP straight to
 * `api.openai.com`, so no proxy path and no prepared host are involved.
 *
 * ⚠ PROD HAS NO KEY — `AppConfiguration/Application/prod/ai-app-config.json`
 * still reads `FILL_ME_IN`. A prod deployment therefore mints nothing, and the
 * WebRTC transport falls back to the same-origin SDP proxy, which DOES need the
 * host to have been prepared. Fill that config in (or set
 * `VITE_CAMPFIRE_MINTING_APP_KEY`) to give prod the infrastructure-free path
 * dev has.
 */
const DEFAULT_MINTING_APP_KEY = "8f8e67b4f1a3c5d29ab7e40c1f3c9d6d4f8a2b1e7c0d5a9f3e4b7c8d1a2f6b9c4e3d2a1b0f5e7c9d8a6b4c2f1e0d9c8b7a6f5e4d3c2b1a0f9e8d7c6b5a4";

function readEnv(name: string): string {
  const value = (import.meta.env as Record<string, unknown>)[name];
  return typeof value === "string" ? value.trim() : "";
}

export function getAiConfig(): AiConfig {
  const enabledRaw = readEnv("VITE_AI_ENABLED").toLowerCase();
  return {
    aiEnabled: enabledRaw === "" || enabledRaw === "true" || enabledRaw === "1",
    aiSvcHost: readEnv("VITE_AISVC_HOST") || DEFAULT_AISVC_HOST,
    realtimeKeyMintingUrl:
      readEnv("VITE_CAMPFIRE_REALTIME_KEY_MINTING_APP_URL") || DEFAULT_MINT_URL,
    mintingAppKey: readEnv("VITE_CAMPFIRE_MINTING_APP_KEY") || DEFAULT_MINTING_APP_KEY,
    responsesAuthAvailable:
      readEnv("VITE_CAMPFIRE_AI_RESPONSES_AUTH").toLowerCase() === "true"
      || readEnv("VITE_CAMPFIRE_AI_RESPONSES_AUTH") === "1",
  };
}

/** Port of the `AIAgentTransportWebSocket` campfire-proxy host check. */
export function isCampfireAiProxyHost(host: string): boolean {
  return host.includes("campfire3d.com");
}

/**
 * Whether a model id can ride the realtime session at all. Measured against
 * the dev proxy (2026-08-13): `/v1/realtime?model=` accepts only the
 * `gpt-realtime*` family and refuses everything else at connect with
 * `invalid_model` ("not supported in realtime mode") — a Responses-family
 * reasoning model (gpt-5.5 / gpt-5.6-sol / gpt-5.6-luna / gpt-5.6-terra)
 * can only be reached over
 * HTTP `/v1/responses`, which is what the orchestrator's text bridge does.
 *
 * Re-measured 2026-08-28, and the earlier note was incomplete rather than
 * wrong: the endpoint also accepts `gpt-realtime-2.1` and
 * `gpt-realtime-2.1-mini`, and — the part that matters downstream — **2.x
 * ACCEPTS `reasoning.effort` where 1.5 refuses it** with `invalid_value`
 * ("Unsupported option for this model"). That refusal is the whole reason
 * `AIOrchestratorModel.handleSessionErrorForModelPolicy` carries an
 * effort-drop self-heal; on the current stock model the effort now reaches
 * the wire and is echoed back in `session.updated`.
 *
 * The predicate itself needs no change for a new version — it is a family
 * prefix test, which is why 2.1 required no edit here.
 */
export function isRealtimeFamilyModel(model: string): boolean {
  return model.trim().toLowerCase().startsWith("gpt-realtime");
}

/** Port of `AIConfigUtility.IsAIEnabledAsync` (config is sync on web). */
export function isAiEnabled(): boolean {
  return getAiConfig().aiEnabled;
}
